/**
 * "Integraciones" — ONE manager for channels and data sources.
 *
 * The owner's ask, twice: *"un administrador de integraciones de
 * comunicación y de fuentes de datos"*, *"la idea es que las
 * integraciones sean todas en la plataforma"*. Before this screen they
 * were two disconnected surfaces buried in a project's hub — a delivery
 * panel and a connections panel — and neither could store the credential
 * it pointed at. This is both of them, in one place, over one idea:
 *
 *   **A credential is registered ONCE and reused.**
 *
 * WHAT EACH TAB SHOWS, AND WHY IT SHOWS "USED BY"
 * -----------------------------------------------
 * Reuse introduces a failure mode that per-consumer credentials did not
 * have: deleting one thing can break several others, somewhere the
 * person deleting is not looking. So every row carries its users
 * (projects, subscriptions), and a delete that would break them is
 * refused by the backend with a message that NAMES them. The list is
 * shown before the click, not only inside the error.
 *
 * WHAT THIS SCREEN REFUSES TO DO
 * ------------------------------
 * - Show a stored value. There is no endpoint that returns one; the row
 *   shows "configured · ****1234" and offers REPLACE.
 * - Offer a WhatsApp form. It has no adapter, and every remaining gate
 *   belongs to Meta. It renders as a "Próximamente" card that says so.
 * - Hide the parts that happen elsewhere. Each card carries the numbered
 *   steps for the customer's own Teams / Slack / Microsoft 365 / cloud
 *   console, served from the backend catalog.
 */

import { Badge, Button, Card, CardBody, Input } from "@omnitok/ui";
import { Disclosure, PageContainer, PageHeader } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import {
  CheckCircle2,
  Clock,
  KeyRound,
  Link2,
  Plus,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import { projectsApi } from "@/lib/builder-api";
import {
  integrationsApi,
  type CatalogCard,
  type ChannelCredential,
  type IntegrationsCatalog,
  type ManagedConnection,
  type SecretStatus,
} from "@/lib/integrations-api";
import ExternalGuidePanel from "@/pages/integrations/ExternalGuidePanel";
import SecretValueForm from "@/pages/integrations/SecretValueForm";
import TeamsWorkflowGuide from "@/pages/delivery/TeamsWorkflowGuide";
import type { DisplayLabel, Project } from "@/types";
import { resolveLocalized } from "@pervasivemind/dashboard-render";

type Tab = "channels" | "sources";

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * "configured · ****1234", or the exact reason it is not.
 *
 * The three sources read differently on purpose. `managed` can show a
 * hint and a version because the store records them; `env` can only say
 * whether the variable is set, and names it, because reading the value
 * to compute a hint is a value read we have no reason to perform;
 * `none` means the connector needs no credential at all, which is a
 * legitimate state and not a gap.
 */
function SecretBadge({ secret }: { secret: SecretStatus }): JSX.Element {
  const { t } = useT();
  if (secret.source === "none") {
    return <Badge>{t("integrations.secret.notNeeded")}</Badge>;
  }
  if (!secret.configured) {
    return (
      <Badge variant="warning">
        <ShieldAlert className="mr-1 inline h-3 w-3" />
        {t("integrations.secret.notConfigured")}
      </Badge>
    );
  }
  return (
    <Badge variant="success">
      <CheckCircle2 className="mr-1 inline h-3 w-3" />
      {t("integrations.secret.configured")}
      {secret.last4 !== "" ? ` · ****${secret.last4}` : ""}
      {secret.version !== null && secret.version > 1 ? ` · v${secret.version}` : ""}
    </Badge>
  );
}

function UsedBy({
  used,
  emptyKey,
}: {
  used: { id: string; label: string }[];
  emptyKey: string;
}): JSX.Element {
  const { t } = useT();
  if (used.length === 0) {
    return (
      <p className="mt-1 text-xs text-neutral-500">{t(emptyKey)}</p>
    );
  }
  return (
    <p className="mt-1 text-xs text-neutral-600">
      <Link2 className="mr-1 inline h-3 w-3" />
      {t("integrations.usedBy", { count: used.length })}{" "}
      <span className="font-mono">{used.map((u) => u.label).join(" · ")}</span>
    </p>
  );
}

export function IntegrationsPage(): JSX.Element {
  const { t, locale } = useT();
  const [tab, setTab] = useState<Tab>("channels");
  const [catalog, setCatalog] = useState<IntegrationsCatalog | null>(null);
  const [credentials, setCredentials] = useState<ChannelCredential[]>([]);
  const [connections, setConnections] = useState<ManagedConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingSecretFor, setEditingSecretFor] = useState<string | null>(null);
  // Migration 035 — the "share with…" picker for an OWNED connection.
  // `projects` feeds the checkbox list; the draft is the full set the
  // operator will save (the API replaces, never appends).
  const [projects, setProjects] = useState<Project[]>([]);
  const [grantingFor, setGrantingFor] = useState<string | null>(null);
  const [grantDraft, setGrantDraft] = useState<string[]>([]);
  const [creating, setCreating] = useState<{
    channel: string;
    name: string;
    description: string;
  } | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // The catalog is fetched in the UI's own locale so the numbered
      // external steps come back in the language the operator is reading.
      const lang = locale;
      const [cat, chans, conns, projs] = await Promise.all([
        integrationsApi.catalog(lang),
        integrationsApi.channels.list(lang),
        integrationsApi.connections.list(lang),
        projectsApi.list(),
      ]);
      setCatalog(cat);
      setCredentials(chans.credentials);
      setConnections(conns.connections);
      setProjects(projs.projects);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [locale]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sourceCards = useMemo(
    () => new Map((catalog?.sources ?? []).map((c) => [c.kind ?? "", c])),
    [catalog],
  );

  const run = async (id: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      // A 409 naming the projects/subscriptions that would break, a 400
      // carrying the secret backend's own actionable message, a 424 from
      // a failed Test — all of them say more than "something went
      // wrong", so all of them are shown verbatim.
      setError(errorText(err));
    } finally {
      setBusyId(null);
    }
  };

  // ── channels ───────────────────────────────────────────────────────

  const renderChannelCard = (card: CatalogCard): JSX.Element => {
    const channel = card.channel ?? "";
    const rows = credentials.filter((c) => c.channel === channel);
    const implemented =
      card.available && (catalog?.implemented_channels ?? []).includes(channel);

    return (
      <Card key={channel} variant="bordered">
        <CardBody>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold">
                {card.label}
                {!implemented && (
                  <Badge data-testid={`coming-soon-${channel}`}>
                    {t("integrations.comingSoon")}
                  </Badge>
                )}
              </h3>
              <p className="mt-1 max-w-2xl text-xs text-neutral-500">
                {card.summary}
              </p>
            </div>
            {implemented && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setCreating({ channel, name: "", description: "" });
                  setEditingSecretFor(null);
                }}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t("integrations.channels.addBtn")}
              </Button>
            )}
          </div>

          {!implemented && card.unavailable_reason !== null && (
            <p
              data-testid={`unavailable-${channel}`}
              className="mt-3 rounded border border-neutral-300 bg-neutral-50 p-2 text-xs text-neutral-700"
            >
              <Clock className="mr-1 inline h-3.5 w-3.5" />
              {card.unavailable_reason}
            </p>
          )}

          {rows.length > 0 && (
            <ul className="mt-3 space-y-2">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="rounded border border-neutral-200 p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">{row.name}</span>
                        <SecretBadge secret={row.secret} />
                        {row.source === "env" && (
                          <Badge>{t("integrations.secret.fromEnv")}</Badge>
                        )}
                      </div>
                      <code className="mt-1 block truncate font-mono text-xs text-neutral-500">
                        {row.secret_ref}
                      </code>
                      {row.secret.detail !== "" && (
                        <p className="mt-1 text-xs text-neutral-500">
                          {row.secret.detail}
                        </p>
                      )}
                      <UsedBy
                        used={row.used_by}
                        emptyKey="integrations.channels.unused"
                      />
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId !== null}
                        onClick={() =>
                          void run(row.id, async () => {
                            const out =
                              await integrationsApi.channels.test(row.id);
                            setNotice(
                              `${row.name}: ${out.message || t("integrations.testOk")}`,
                            );
                          })
                        }
                      >
                        {busyId === row.id
                          ? t("integrations.testing")
                          : t("integrations.testBtn")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCreating(null);
                          setEditingSecretFor(
                            editingSecretFor === row.id ? null : row.id,
                          );
                        }}
                      >
                        <KeyRound className="mr-1 h-3.5 w-3.5" />
                        {row.secret.configured
                          ? t("integrations.secret.replaceBtn")
                          : t("integrations.secret.setBtn")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId !== null}
                        onClick={() => {
                          if (
                            !window.confirm(
                              t("integrations.channels.deleteConfirm", {
                                name: row.name,
                              }),
                            )
                          )
                            return;
                          void run(row.id, () =>
                            integrationsApi.channels.remove(row.id),
                          );
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        <span className="sr-only">
                          {t("integrations.deleteBtn")}
                        </span>
                      </Button>
                    </div>
                  </div>

                  {editingSecretFor === row.id && (
                    <SecretValueForm
                      fields={card.fields}
                      saving={busyId === row.id}
                      replacing={row.secret.configured}
                      onCancel={() => setEditingSecretFor(null)}
                      onSubmit={(values) => {
                        void run(row.id, async () => {
                          await integrationsApi.channels.replaceSecret(
                            row.id,
                            values,
                          );
                          setEditingSecretFor(null);
                          setNotice(
                            t("integrations.secret.stored", { name: row.name }),
                          );
                        });
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          {creating !== null && creating.channel === channel && (
            <div className="mt-3 space-y-3 rounded border border-neutral-300 p-3">
              <label className="block text-xs">
                {t("integrations.channels.nameLabel")}
                <Input
                  className="mt-1"
                  value={creating.name}
                  placeholder="ops-room"
                  onChange={(e) =>
                    setCreating({ ...creating, name: e.target.value })
                  }
                />
                <span className="mt-1 block text-xs text-neutral-500">
                  {t("integrations.channels.nameHint")}
                </span>
              </label>
              <label className="block text-xs">
                {t("integrations.channels.descriptionLabel")}
                <Input
                  className="mt-1"
                  value={creating.description}
                  onChange={(e) =>
                    setCreating({ ...creating, description: e.target.value })
                  }
                />
              </label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={busyId !== null || creating.name.trim() === ""}
                  onClick={() =>
                    void run("create", async () => {
                      await integrationsApi.channels.create({
                        channel,
                        name: creating.name.trim(),
                        description: creating.description.trim(),
                      });
                      setCreating(null);
                    })
                  }
                >
                  {t("integrations.channels.createBtn")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCreating(null)}
                >
                  {t("integrations.cancelBtn")}
                </Button>
              </div>
            </div>
          )}

          {/* Teams reuses the guide that shipped in Plan C fase 5 rather
              than a second copy of Microsoft's menu path. Every other
              channel renders the server catalog's steps. */}
          {card.guide_component === "teams_workflow" ? (
            <div className="mt-3">
              <TeamsWorkflowGuide
                secretRef={rows[0]?.secret_ref ?? ""}
                storedInWeb={rows.some((r) => r.source === "managed")}
              />
            </div>
          ) : (
            card.guide !== null && <ExternalGuidePanel guide={card.guide} />
          )}
        </CardBody>
      </Card>
    );
  };

  // ── data sources ───────────────────────────────────────────────────

  const renderConnection = (row: ManagedConnection): JSX.Element => {
    const card = sourceCards.get(row.kind) ?? null;
    const shared = row.project_id === null;
    return (
      <li key={row.id} className="rounded border border-neutral-200 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{row.name}</span>
              <Badge>{row.kind}</Badge>
              <Badge variant={shared ? "success" : undefined}>
                {shared
                  ? t("integrations.sources.shared")
                  : t("integrations.sources.ownedBy", {
                      slug: row.project_slug ?? row.project_id ?? "?",
                    })}
              </Badge>
              {row.shared_project_ids.length > 0 && (
                <Badge title={row.shared_project_slugs.join(", ")}>
                  {t("integrations.sources.alsoShared", {
                    count: row.shared_project_ids.length,
                  })}
                </Badge>
              )}
              <SecretBadge secret={row.secret} />
            </div>
            <code className="mt-1 block truncate font-mono text-xs text-neutral-500">
              {row.secret_ref || "—"}
            </code>
            {row.secret.detail !== "" && (
              <p className="mt-1 text-xs text-neutral-500">
                {row.secret.detail}
              </p>
            )}
            <UsedBy used={row.used_by} emptyKey="integrations.sources.unused" />
          </div>
          <div className="flex shrink-0 gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={busyId !== null}
              onClick={() =>
                void run(row.id, async () => {
                  const out = await integrationsApi.connections.test(row.id);
                  setNotice(
                    `${row.name}: ${
                      out.ok
                        ? t("integrations.testOk")
                        : t("integrations.testFailed")
                    } — ${JSON.stringify(out.detail)}`,
                  );
                })
              }
            >
              {busyId === row.id
                ? t("integrations.testing")
                : t("integrations.testBtn")}
            </Button>
            {!shared && (
              <Button
                size="sm"
                variant="outline"
                disabled={busyId !== null}
                onClick={() =>
                  void run(row.id, () =>
                    integrationsApi.connections.setScope(row.id, null),
                  )
                }
              >
                {t("integrations.sources.shareBtn")}
              </Button>
            )}
            {!shared && (
              <Button
                size="sm"
                variant="outline"
                disabled={busyId !== null}
                onClick={() => {
                  if (grantingFor === row.id) {
                    setGrantingFor(null);
                    return;
                  }
                  setGrantDraft(row.shared_project_ids);
                  setGrantingFor(row.id);
                }}
              >
                {t("integrations.sources.grantBtn")}
              </Button>
            )}
            {card !== null && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setEditingSecretFor(
                    editingSecretFor === row.id ? null : row.id,
                  )
                }
              >
                <KeyRound className="mr-1 h-3.5 w-3.5" />
                {row.secret.configured && row.secret.source === "managed"
                  ? t("integrations.secret.replaceBtn")
                  : t("integrations.secret.setBtn")}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={busyId !== null}
              onClick={() => {
                if (
                  !window.confirm(
                    t("integrations.sources.deleteConfirm", { name: row.name }),
                  )
                )
                  return;
                void run(row.id, () =>
                  integrationsApi.connections.remove(row.id),
                );
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span className="sr-only">{t("integrations.deleteBtn")}</span>
            </Button>
          </div>
        </div>

        {grantingFor === row.id && !shared && (
          <div
            data-testid="connection-grant-picker"
            className="mt-3 rounded border border-neutral-200 bg-neutral-50 p-3"
          >
            <p className="text-xs font-semibold">
              {t("integrations.sources.grantTitle", { name: row.name })}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              {t("integrations.sources.grantHint", {
                slug: row.project_slug ?? row.project_id ?? "?",
              })}
            </p>
            {projects.filter((p) => p.id !== row.project_id).length === 0 ? (
              <p className="mt-2 text-xs text-neutral-500">
                {t("integrations.sources.grantNoOthers")}
              </p>
            ) : (
              <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                {projects
                  .filter((p) => p.id !== row.project_id)
                  .map((p) => {
                    const checked = grantDraft.includes(p.id);
                    return (
                      <li key={p.id}>
                        <label className="flex cursor-pointer items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={busyId !== null}
                            onChange={() =>
                              setGrantDraft((prev) =>
                                checked
                                  ? prev.filter((id) => id !== p.id)
                                  : [...prev, p.id],
                              )
                            }
                          />
                          <span className="font-medium">{p.slug}</span>
                          {/* ``project.name`` is a TEXT column that may
                              carry a JSON-encoded bilingual label — the
                              same resolution ProjectHubPage applies, or
                              the operator reads {"en":…,"es":…} raw. */}
                          <span className="truncate text-neutral-500">
                            {resolveLocalized(p.name as DisplayLabel, locale)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
              </ul>
            )}
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                variant="primary"
                disabled={busyId !== null}
                onClick={() =>
                  void run(row.id, async () => {
                    await integrationsApi.connections.setProjects(
                      row.id,
                      grantDraft,
                    );
                    setGrantingFor(null);
                    setNotice(
                      t("integrations.sources.grantSaved", { name: row.name }),
                    );
                  })
                }
              >
                {t("integrations.sources.grantSave")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busyId !== null}
                onClick={() => setGrantingFor(null)}
              >
                {t("integrations.cancelBtn")}
              </Button>
            </div>
          </div>
        )}

        {editingSecretFor === row.id && card !== null && (
          <SecretValueForm
            fields={card.fields}
            saving={busyId === row.id}
            replacing={
              row.secret.configured && row.secret.source === "managed"
            }
            onCancel={() => setEditingSecretFor(null)}
            onSubmit={(values) => {
              void run(row.id, async () => {
                await integrationsApi.connections.replaceSecret(
                  row.id,
                  values,
                );
                setEditingSecretFor(null);
                setNotice(
                  t("integrations.secret.stored", { name: row.name }),
                );
              });
            }}
          />
        )}
      </li>
    );
  };

  // ── shell ──────────────────────────────────────────────────────────

  return (
    /* The only page in the builder whose title was two steps smaller
       than every other, in a container that ran full-bleed while the
       rest sits at 1152 px (2026-09 audit, T2 and T4). The four-line
       intro is now one sentence; the rest of it moved into the section
       that actually documents each channel. */
    <PageContainer>
      <PageHeader
        title={t("integrations.title")}
        subtitle={t("integrations.introShort")}
      />

      {catalog !== null && !catalog.secret_backend_configured && (
        <Card variant="bordered">
          <CardBody>
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <ShieldAlert className="h-4 w-4" />
              {t("integrations.backend.title")}
            </h2>
            <p className="mt-1 text-xs text-neutral-600">
              {t("integrations.backend.intro")}
            </p>
            {/* Served verbatim from tenancy.secret_store.setup_guide —
                the one part of integrations that genuinely cannot be
                done from this web, because the backend a web form writes
                to has to be switched on by deploy-time variables. */}
            <ul
              data-testid="secret-backend-setup"
              className="mt-2 space-y-1 text-xs text-neutral-700"
            >
              {catalog.secret_backend_setup.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <div className="flex gap-2 border-b border-neutral-200">
        {(["channels", "sources"] as Tab[]).map((id) => (
          <button
            key={id}
            type="button"
            data-testid={`integrations-tab-${id}`}
            className={
              tab === id
                ? "border-b-2 border-accent px-3 py-2 text-sm font-medium text-accent"
                : "px-3 py-2 text-sm text-neutral-500 hover:text-neutral-800"
            }
            onClick={() => {
              setTab(id);
              setEditingSecretFor(null);
              setCreating(null);
            }}
          >
            {t(`integrations.tabs.${id}`)}
          </button>
        ))}
      </div>

      {error !== null && (
        <p
          role="alert"
          className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700"
        >
          {error}
        </p>
      )}
      {notice !== null && (
        <p
          role="status"
          className="rounded border border-neutral-300 bg-neutral-50 p-2 text-xs text-neutral-700"
        >
          {notice}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : tab === "channels" ? (
        <div className="space-y-4">
          {(catalog?.channels ?? []).map((card) => renderChannelCard(card))}
        </div>
      ) : (
        <Card variant="bordered">
          <CardBody>
            <h3 className="text-sm font-semibold">
              {t("integrations.sources.title")}
            </h3>
            <p className="mt-1 max-w-3xl text-xs text-neutral-500">
              {t("integrations.sources.intro")}
            </p>
            {connections.length === 0 ? (
              <p className="mt-4 text-xs text-neutral-500">
                {t("integrations.sources.empty")}
              </p>
            ) : (
              <ul className="mt-4 space-y-2">
                {connections.map((row) => renderConnection(row))}
              </ul>
            )}
            <div className="mt-4 space-y-3">
              {(catalog?.sources ?? []).map(
                (card) =>
                  card.guide !== null && (
                    <Disclosure
                      key={card.kind}
                      summary={`${card.label} — ${t("integrations.guide.title")}`}
                    >
                      <ExternalGuidePanel guide={card.guide} />
                    </Disclosure>
                  ),
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </PageContainer>
  );
}

export default IntegrationsPage;
